import Papa from "papaparse";
import type { ColumnDefinition, DataRow } from "../../schema/chart";
import { detectColumns } from "./columnHelpers";

/**
 * Parse pasted text as TSV (tab-separated) or CSV (comma-separated).
 * Tries TSV first (if tabs are present), then falls back to CSV via PapaParse.
 */
export function parseTSV(text: string): { columns: ColumnDefinition[]; rows: DataRow[] } | null {
    // Try TSV first if tabs are present
    if (text.includes("\t")) {
        const result = parseTSVStrict(text);
        if (result) return result;
    }

    // Fall back to CSV via PapaParse (handles commas, quoted fields, etc.)
    return parseCSV(text);
}

function parseTSVStrict(text: string): { columns: ColumnDefinition[]; rows: DataRow[] } | null {
    const lines = text
        .replace(/\r/g, "")
        .split("\n")
        .filter((line) => line.trim() !== "");

    if (lines.length < 2) return null;

    const names = lines[0].split("\t");
    if (names.length < 2) return null;

    const rawRows: Record<string, string>[] = [];
    for (let i = 1; i < lines.length; i++) {
        const cells = lines[i].split("\t");
        const rawRow: Record<string, string> = {};
        names.forEach((col, colIdx) => {
            rawRow[col] = cells[colIdx] ?? "";
        });
        rawRows.push(rawRow);
    }

    const columns = detectColumns(names, rawRows);
    return { columns, rows: rawRows };
}

function parseCSV(text: string): { columns: ColumnDefinition[]; rows: DataRow[] } | null {
    const result = Papa.parse<Record<string, string>>(text, {
        header: true,
        skipEmptyLines: true,
    });

    if (!result.data?.length || !result.meta?.fields?.length) return null;
    if (result.meta.fields.length < 2) return null;

    const names = result.meta.fields;
    const rawRows = result.data;
    const columns = detectColumns(names, rawRows);

    return { columns, rows: rawRows };
}
